import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ClipboardList,
  FileText,
  History,
  Pen,
  Send,
  Trash2,
  Undo2,
  Upload,
} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  ConfirmationDialog,
  DatePicker,
  Label,
  Textarea,
} from '@roger/ui-components';
import {
  approveTask,
  breakTaskSignatures,
  getMiscTask,
  getTaskSignatures,
  returnTaskToClinician,
  signTask,
  updateMiscTask,
} from '../../services/api';
import type {
  MiscTaskResponse,
  TaskSignatureResponse,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { useToast } from '../../hooks/useToastContext';
import { useAllowSubmitWithoutSignature } from '../../hooks/useAllowSubmitWithoutSignature';
import { useAutoSave, SaveDeclinedError } from '../../hooks/useAutoSave';
import {
  getSignButtonLabel,
  useTaskPermissions,
} from '../task/TaskDetailPanel';
import {
  TaskHeaderControls,
  TaskWorkflowStatusBadge,
} from '../task/TaskHeaderControls';
import { AttestationDialog } from '../signing/AttestationDialog';
import type {
  ESignatureCredentialInput,
  SignatureDateInput,
} from '../signing/esignatureCredentialApi';
import { PermissionSlug } from '../../constants/featureFlags';
import { SignatureHistoryDialog } from '../signing/SignatureHistoryDialog';
import { WorkLogDialog } from '../task/WorkLogDialog';
import { useDeferredDialogOpener } from '../task/useDeferredDialogOpener';
import {
  resolveAttestationFamily,
  selectAttestationForFamily,
} from '../signing/attestations';
import { useActiveAttestations } from '../signing/useActiveAttestations';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import EpisodeNotes from '../episode/EpisodeNotes';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { AutoSavePill } from '../AutoSavePill';
import { useTaskDocuments } from '../documents/useTaskDocuments';
import { useTaskSignatureBypasses } from '../signing/useTaskSignatureBypasses';
import { useSignatureBypassAction } from '../signing/useSignatureBypassAction';

interface MiscTaskDetailPanelProps {
  miscTaskId: string;
  /** Owning patient, forwarded so a background document upload also refreshes
   * the patient page's Documents-count badge (via patientUploadTick). */
  patientId?: string;
  onBack?: () => void;
  onUpdated?: () => void;
  /**
   * This task is soft-deleted, opened from the deleted-tasks view. Widens the
   * reads that would otherwise 404 and makes the panel strictly read only: no
   * editable fields, no autosave, no signing, no unlock, no document uploads or
   * deletions. The Actions menu keeps only signature history and the work log.
   */
  isDeleted?: boolean;
}

type MiscTaskDetailDraft = {
  task_date: string | null;
  notes: string;
};

const emptyMiscTaskDetailDraft: MiscTaskDetailDraft = {
  task_date: null,
  notes: '',
};

const draftFromResponse = (task: MiscTaskResponse): MiscTaskDetailDraft => ({
  task_date: task.task_date,
  notes: task.notes ?? '',
});

// useAutoSave defaults to Object.is. This draft is a new object on every fetch /
// PATCH response, so reference equality would treat every echo as a fresh
// server snapshot and rewrite the textarea after each save.
const miscTaskDetailDraftsEqual = (
  a: MiscTaskDetailDraft,
  b: MiscTaskDetailDraft
): boolean =>
  a.task_date === b.task_date &&
  // Save persists notes.trim() || null; compare trimmed so a trailing space
  // does not look unequal to the echo and get rewritten into the textarea
  // after save.
  a.notes.trim() === b.notes.trim();

/**
 * Detail panel for misc tasks (Admission Consents and similar): a date, a notes
 * box, and attached documents. Deliberately has no physician field and no print
 * view — misc tasks carry the uploaded document as the record.
 */
export const MiscTaskDetailPanel: React.FC<MiscTaskDetailPanelProps> = ({
  miscTaskId,
  patientId,
  onBack,
  onUpdated,
  isDeleted = false,
}) => {
  const { error: showError } = useToast();
  const user = useStore(state => state.user);
  const allowUnsignedMoves = useAllowSubmitWithoutSignature();
  const {
    attestations,
    isLoading: attestationsLoading,
    error: attestationsError,
  } = useActiveAttestations();

  useEffect(() => {
    if (attestationsError) {
      showError(
        'Could not load signing text',
        'Refresh the page or try again in a moment.'
      );
    }
  }, [attestationsError, showError]);

  const [miscTask, setMiscTask] = useState<MiscTaskResponse | null>(null);
  const [signatures, setSignatures] = useState<TaskSignatureResponse[]>([]);
  // ROG-3269: a bypassed chart must not render as normally signed. Keyed on an
  // explicit nonce rather than `signatures.length` — a bypass writes no
  // signature, so a count would never change and the notice would not appear
  // until the panel remounted.
  const [signatureRefreshNonce, setSignatureRefreshNonce] = useState(0);
  const signatureBypasses = useTaskSignatureBypasses(
    miscTaskId,
    signatureRefreshNonce
  );
  const [loading, setLoading] = useState(true);
  const [signingLoading, setSigningLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [returning, setReturning] = useState(false);
  const [breaking, setBreaking] = useState(false);
  const [approveAttestationOpen, setApproveAttestationOpen] = useState(false);
  const [signAttestationOpen, setSignAttestationOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const openDeferred = useDeferredDialogOpener();
  const [workLogOpen, setWorkLogOpen] = useState(false);
  const canViewWorkLog = useStore(state =>
    state.hasPermission(PermissionSlug.WORKLOG_VIEW)
  );
  const [pendingDeleteDocId, setPendingDeleteDocId] = useState<string | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [savedDraft, setSavedDraft] = useState<MiscTaskDetailDraft>(
    emptyMiscTaskDetailDraft
  );

  // PatientDetail swaps miscTaskId on the mounted panel rather than remounting
  // it, so the previous task's data must be cleared synchronously. Leaving
  // savedDraft behind is not just a stale display: useAutoSave re-seeds its
  // local value and saved baseline whenever initialValue changes, so the form
  // would show the old task's date and notes while handleAutoSave PATCHes the
  // new miscTaskId — writing one task's content onto another.
  const prevMiscTaskIdRef = useRef(miscTaskId);
  if (prevMiscTaskIdRef.current !== miscTaskId) {
    prevMiscTaskIdRef.current = miscTaskId;
    setMiscTask(null);
    setSignatures([]);
    setSavedDraft(emptyMiscTaskDetailDraft);
    setLoading(true);
  }

  // Monotonic per-request token, same reason as in useTaskDocuments: the reset
  // above is synchronous, but a load already in flight resolves afterward with
  // the previous task's data.
  const taskRequestSeqRef = useRef(0);

  const {
    documents,
    loading: documentsLoading,
    deleting,
    hasFetched: documentsFetched,
    loadFailed: documentsLoadFailed,
    uploadPending: documentsUploadPending,
    fetchDocuments,
    openDocument,
    uploadDocument,
    deleteDocument,
  } = useTaskDocuments(miscTaskId, onUpdated, {
    patientId,
    targetLabel: miscTask?.misc_task_type,
    includeDeleted: isDeleted,
  });

  // The misc attestation covers "any document attached to this task", and the
  // document set is hashed into the signature, so there is nothing meaningful to
  // attest to until we know what is attached. Three ways that fails, each with
  // its own copy so the next action is obvious:
  //
  // - the list never loaded, so we cannot show the clinician what they would be
  //   attesting to;
  // - nothing is attached yet. The server rejects this too, so the gate only
  //   spares the clinician a round trip into an error toast;
  // - an upload is still in flight. Uploads are fire-and-forget, so without this
  //   a clinician can choose a file and sign before it lands, signing without the
  //   document they meant to attach or locking the task out from under the upload.
  const documentsUnknown = !documentsFetched || documentsLoadFailed;
  const documentsEmpty = !documentsUnknown && documents.length === 0;
  const documentsUnverified =
    documentsUnknown || documentsEmpty || documentsUploadPending;
  const documentsUnverifiedReason = documentsUploadPending
    ? 'Wait for the document upload to finish before signing.'
    : documentsUnknown
      ? 'Documents could not be loaded. Retry before signing.'
      : documentsEmpty
        ? 'Attach a document before signing.'
        : undefined;

  const fetchData = async () => {
    const seq = (taskRequestSeqRef.current += 1);
    const requestedTaskId = miscTaskId;
    const isCurrent = () =>
      taskRequestSeqRef.current === seq &&
      prevMiscTaskIdRef.current === requestedTaskId;

    setLoading(true);
    try {
      // throwOnError: the generated client resolves a 4xx/5xx with `data`
      // undefined, so without it the `if (res.data)` guards below silently kept
      // whatever was on screen — a failed load for this task would leave the
      // previous one displayed as if it were this one.
      const [taskRes, sigsRes] = await Promise.all([
        getMiscTask({
          path: { misc_task_id: requestedTaskId },
          ...(isDeleted && { query: { include_deleted: true } }),
          throwOnError: true,
        }),
        getTaskSignatures({
          path: { task_id: requestedTaskId },
          ...(isDeleted && { query: { include_deleted: true } }),
          throwOnError: true,
        }),
      ]);
      if (!isCurrent()) return;
      if (taskRes.data) {
        setMiscTask(taskRes.data);
        setSavedDraft(draftFromResponse(taskRes.data));
      }
      if (sigsRes.data) {
        setSignatures(sigsRes.data);
      }
      setSignatureRefreshNonce(n => n + 1);
    } catch (err) {
      // A superseded failure belongs to the task the user navigated away from.
      if (!isCurrent()) return;
      console.error('Error fetching misc task:', err);
      showError('Load Failed', 'Failed to load task details.');
    } finally {
      // The newest load owns the spinner.
      if (isCurrent()) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void fetchData();
    void fetchDocuments();
  }, [miscTaskId, isDeleted]);

  // A deleted misc task is never editable, whatever status it was deleted in.
  // Deriving read-only from this (rather than only hiding controls) is what
  // keeps the editable fields and their autosave off the page entirely.
  const isReadonly =
    isDeleted || !EDITABLE_STATUSES.has(miscTask?.status ?? 'not_started');

  const {
    canSign: canSignTask,
    canApprove: canApproveTask,
    canBreakSignatures: canBreakTaskSignatures,
    canBypassSignature: canBypassTaskSignature,
    canReturnToClinician: canReturnTaskToClinician,
    signRequiresESignature,
    signTargetStatus,
  } = useTaskPermissions(user, miscTask ?? undefined, {
    allowSubmitWithoutSignature: allowUnsignedMoves,
  });

  // A deleted misc task forfeits every workflow action, regardless of role.
  // Zeroed here rather than at each use site so a new action added below
  // inherits the suppression instead of having to remember it.
  const canSign = !isDeleted && canSignTask;
  const canApprove = !isDeleted && canApproveTask;
  const canBreakSignatures = !isDeleted && canBreakTaskSignatures;
  const canBypassSignature = !isDeleted && canBypassTaskSignature;
  const canReturnToClinician = !isDeleted && canReturnTaskToClinician;

  // A misc task can await a clinician who will never sign it, same as a visit.
  const { actionItems: bypassActionItems, dialog: bypassDialog } =
    useSignatureBypassAction({
      taskId: miscTaskId,
      canBypass: canBypassSignature,
      onBypassed: fetchData,
    });

  const miscAttestation = useMemo(() => {
    if (!attestations) return null;
    return selectAttestationForFamily(
      attestations,
      resolveAttestationFamily('misc_task')
    );
  }, [attestations]);

  const activeSignatures = useMemo(
    () => signatures.filter(sig => !sig.invalidated_at),
    [signatures]
  );

  const handleAutoSave = React.useCallback(
    async (draft: MiscTaskDetailDraft) => {
      if (!miscTask || !draft.task_date) {
        // Refuse the save by throwing rather than returning, so useAutoSave does
        // not record an incomplete draft as the saved baseline.
        throw new SaveDeclinedError();
      }

      try {
        const res = await updateMiscTask({
          path: { misc_task_id: miscTaskId },
          body: {
            task_date: draft.task_date,
            notes: draft.notes.trim() || null,
          },
        });
        if (!res.data) {
          throw new Error('Misc task update returned no data.');
        }
        // The panel may have switched tasks while this PATCH was in flight. The
        // save itself is still good — it landed on the task it was addressed to,
        // so useAutoSave should treat it as saved — but applying the row would
        // show that task's date and notes under the one now on screen, and
        // re-seed useAutoSave's baseline from them.
        if (prevMiscTaskIdRef.current === miscTaskId) {
          setMiscTask(res.data);
          // Keep initialValue on the row we just wrote. Leaving the fetch-time
          // savedDraft in place makes useAutoSave adopt that stale object once
          // the field goes clean after save, which reverts the notes textarea
          // while the pill still shows Autosaved.
          setSavedDraft(draftFromResponse(res.data));
        }
        onUpdated?.();
      } catch (err) {
        console.error('Error saving misc task:', err);
        showError('Save Failed', 'Could not save changes.');
        throw err;
      }
    },
    [miscTask, miscTaskId, onUpdated, showError]
  );

  const {
    value: editDraft,
    setValue: setEditDraft,
    saveStatus,
    saveNow,
    changeToken,
  } = useAutoSave({
    initialValue: savedDraft,
    onSave: handleAutoSave,
    isEqual: miscTaskDetailDraftsEqual,
  });

  const editDate = useMemo(
    () =>
      editDraft.task_date
        ? parseISOToLocalDate(editDraft.task_date)
        : undefined,
    [editDraft.task_date]
  );

  const updateEditDraft = React.useCallback(
    (patch: Partial<MiscTaskDetailDraft>) => {
      setEditDraft({ ...editDraft, ...patch });
    },
    [editDraft, setEditDraft]
  );

  const doSignAndSubmit = async (
    credential: ESignatureCredentialInput,
    signatureDate?: SignatureDateInput
  ) => {
    if (!miscAttestation) return;
    setSigningLoading(true);
    try {
      // Flush pending edits first and bail if they did not persist, so we never
      // sign stale data. onSave already surfaced an error toast.
      const outcome = await saveNow();
      if (outcome !== 'saved') {
        // The save was declined or failed, which shows no toast of its own. The
        // dialog stays open only to preserve a rejected password, and there is
        // no password to preserve here — leaving it open would sit there doing
        // nothing on every further click.
        setSignAttestationOpen(false);
        return;
      }
      const result = await signTask({
        path: { task_id: miscTaskId },
        body: {
          ...credential,
          ...signatureDate,
          attestation_version: miscAttestation.version,
        },
      });
      if (result.error) {
        console.error('Error signing misc task:', result.error);
        showError(
          'Sign Failed',
          getBusinessErrorMessage(result) ?? 'Failed to sign. Please try again.'
        );
        return;
      }
      await fetchData();
      onUpdated?.();
      // Closed only on success, so a rejected e-signature password leaves the
      // dialog and what was typed in place.
      setSignAttestationOpen(false);
    } catch (err) {
      console.error('Error signing misc task:', err);
      showError('Sign Failed', 'Failed to sign. Please try again.');
    } finally {
      setSigningLoading(false);
    }
  };

  const handleOpenSignAttestation = async () => {
    const outcome = await saveNow();
    if (outcome !== 'saved') return;
    setSignAttestationOpen(true);
  };

  const doApprove = async (credential: ESignatureCredentialInput) => {
    if (!miscAttestation) return;
    setApproving(true);
    try {
      const result = await approveTask({
        path: { task_id: miscTaskId },
        body: { ...credential, attestation_version: miscAttestation.version },
      });
      if (result.error) {
        console.error('Error approving misc task:', result.error);
        showError(
          'Approve Failed',
          getBusinessErrorMessage(result) ??
            'Failed to complete. Please try again.'
        );
        return;
      }
      await fetchData();
      onUpdated?.();
      setApproveAttestationOpen(false);
    } catch (err) {
      console.error('Error approving misc task:', err);
      showError('Approve Failed', 'Failed to complete. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  const handleBreakSignatures = async () => {
    setBreaking(true);
    try {
      const result = await breakTaskSignatures({
        path: { task_id: miscTaskId },
      });
      if (result.error) {
        console.error('Error unlocking misc task:', result.error);
        showError('Unlock Failed', 'Failed to unlock. Please try again.');
        return;
      }
      await fetchData();
      onUpdated?.();
    } catch (err) {
      console.error('Error unlocking misc task:', err);
      showError('Unlock Failed', 'Failed to unlock. Please try again.');
    } finally {
      setBreaking(false);
    }
  };

  // ROG-3268 / EMR-1467 — a case manager hands the task back to the clinician
  // instead of approving it. Same task-generic endpoint as the visit panel.
  const handleReturnToClinician = async () => {
    setReturning(true);
    try {
      const result = await returnTaskToClinician({
        path: { task_id: miscTaskId },
      });
      if (result.error) {
        console.error('Error returning misc task to clinician:', result.error);
        showError(
          'Return Failed',
          getBusinessErrorMessage(result) ??
            'Failed to return the task. Please try again.'
        );
        return;
      }
      await fetchData();
      onUpdated?.();
    } catch (err) {
      console.error('Error returning misc task to clinician:', err);
      showError(
        'Return Failed',
        'Failed to return the task. Please try again.'
      );
    } finally {
      setReturning(false);
    }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so re-selecting the same file fires onChange again.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (!file) return;
    // Fire-and-forget: a persistent toast tracks progress and the hook refreshes
    // the list once the upload lands, so the panel stays usable meanwhile.
    uploadDocument(file);
  };

  const handleDeleteDocument = async () => {
    if (!pendingDeleteDocId) return;
    await deleteDocument(pendingDeleteDocId);
    setPendingDeleteDocId(null);
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4' />
          <p className='text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  if (!miscTask) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-muted-foreground'>Task not found.</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full'>
      {/* Header */}
      <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3 border-b bg-muted/20'>
        <div className='flex min-w-0 flex-1 items-center gap-3'>
          {onBack && (
            <Button
              variant='outline'
              size='sm'
              onClick={onBack}
              className='flex items-center gap-1.5 flex-shrink-0'
            >
              <ArrowLeft className='h-3.5 w-3.5' />
              Back
            </Button>
          )}
          <div className='h-4 w-px bg-border' />
          <div className='min-w-0 flex-1'>
            <div className='truncate text-sm font-medium text-foreground'>
              {miscTask.misc_task_type}
            </div>
            <div className='text-xs text-muted-foreground'>
              Date: {formatDateToMMDDYYYY(miscTask.task_date)}
            </div>
          </div>
        </div>

        <TaskHeaderControls
          className='w-full sm:w-auto'
          statusItems={
            <TaskWorkflowStatusBadge
              status={miscTask.status}
              signatures={activeSignatures}
              bypasses={signatureBypasses}
            />
          }
          primaryActions={
            <>
              {canBreakSignatures && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => void handleBreakSignatures()}
                  disabled={breaking}
                  className='flex items-center gap-1.5'
                >
                  <Pen className='h-4 w-4' />
                  {breaking ? 'Unlocking...' : 'Unlock & Edit'}
                </Button>
              )}
              {canSign && (
                <Button
                  onClick={() => void handleOpenSignAttestation()}
                  disabled={
                    signingLoading ||
                    attestationsLoading ||
                    !miscAttestation ||
                    saveStatus === 'saving' ||
                    documentsUnverified
                  }
                  title={documentsUnverifiedReason}
                  size='sm'
                  variant='default'
                  className='flex items-center gap-1.5'
                >
                  {signingLoading ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                      Signing...
                    </>
                  ) : (
                    <>
                      <Send className='h-4 w-4' />
                      Sign &amp; Submit
                    </>
                  )}
                </Button>
              )}
              {canApprove && (
                <Button
                  onClick={() => setApproveAttestationOpen(true)}
                  disabled={
                    approving ||
                    attestationsLoading ||
                    !miscAttestation ||
                    documentsUnverified
                  }
                  title={documentsUnverifiedReason}
                  size='sm'
                  variant='default'
                  className='flex items-center gap-1.5'
                >
                  {approving ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                      Approving...
                    </>
                  ) : (
                    <>
                      <Send className='h-4 w-4' />
                      Sign &amp; Complete
                    </>
                  )}
                </Button>
              )}
            </>
          }
          secondaryActions={[
            {
              label: 'View Signature History',
              icon: <History className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () =>
                openDeferred('history', () => setHistoryOpen(true)),
            },
            ...(canViewWorkLog
              ? [
                  {
                    label: 'Work log',
                    icon: (
                      <ClipboardList
                        className='h-4 w-4 mr-2'
                        aria-hidden='true'
                      />
                    ),
                    onSelect: () =>
                      openDeferred('workLog', () => setWorkLogOpen(true)),
                  },
                ]
              : []),
            ...bypassActionItems,
            ...(canReturnToClinician
              ? [
                  {
                    label: returning ? 'Returning…' : 'Return to clinician',
                    icon: <Undo2 className='h-4 w-4 mr-2' aria-hidden='true' />,
                    onSelect: () => void handleReturnToClinician(),
                    disabled: returning,
                  },
                ]
              : []),
          ]}
        />
      </div>

      {miscTask.imported_at ? (
        <div className='mx-4 my-3 rounded-md border border-border bg-muted/40 p-3 text-sm'>
          <p className='font-medium'>Imported from External EMR</p>
          <p>
            {miscTask.kinnser_task_name || miscTask.misc_task_type}
            {miscTask.kinnser_task_status
              ? ` · Source status: ${miscTask.kinnser_task_status}`
              : ''}
          </p>
          <p className='mt-1 text-muted-foreground'>
            Source completion does not record a Roger signature. Signature
            history shows signatures recorded in Roger.
          </p>
        </div>
      ) : null}

      {/* Save status pill: a zero-height sticky overlay above the scrollable body,
          so showing/hiding it never shifts the layout. */}
      {!isReadonly && (
        <div className='pointer-events-none sticky top-0 z-20 flex h-0 shrink-0 justify-end px-5'>
          <AutoSavePill
            status={editDraft.task_date ? saveStatus : 'idle'}
            debounceMs={1500}
            changeToken={changeToken}
          />
        </div>
      )}

      {/* Body */}
      <div className='flex-1 overflow-auto p-4 space-y-4'>
        <Card>
          <CardContent className='p-4 space-y-4'>
            {isReadonly ? (
              <>
                <div className='grid grid-cols-2 gap-4 text-sm'>
                  <div>
                    <span className='font-medium text-muted-foreground'>
                      Date
                    </span>
                    <p>{formatDateToMMDDYYYY(miscTask.task_date)}</p>
                  </div>
                  <div>
                    <span className='font-medium text-muted-foreground'>
                      Type
                    </span>
                    <p>{miscTask.misc_task_type}</p>
                  </div>
                </div>
                {miscTask.notes && (
                  <div>
                    <span className='text-sm font-medium text-muted-foreground'>
                      Notes
                    </span>
                    <div className='mt-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                      {miscTask.notes}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className='space-y-1.5'>
                  <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                    Date
                  </Label>
                  <DatePicker
                    date={editDate}
                    onDateChange={nextDate =>
                      updateEditDraft({
                        task_date: nextDate ? formatDateToISO(nextDate) : null,
                      })
                    }
                    minDate={
                      miscTask.episode_start_date
                        ? parseISOToLocalDate(miscTask.episode_start_date)
                        : undefined
                    }
                    maxDate={
                      miscTask.episode_end_date
                        ? parseISOToLocalDate(miscTask.episode_end_date)
                        : undefined
                    }
                  />
                </div>
                <div className='space-y-1.5'>
                  <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                    Notes
                  </Label>
                  <Textarea
                    value={editDraft.notes}
                    onChange={e => updateEditDraft({ notes: e.target.value })}
                    onBlur={() => void saveNow()}
                    placeholder='Any notes about these documents…'
                    className='min-h-[120px] resize-y text-sm'
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4 space-y-3'>
            <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              Documents
            </Label>
            {documentsLoading ? (
              <p className='text-sm text-muted-foreground'>Loading...</p>
            ) : documentsLoadFailed ? (
              // Never fall through to "No documents attached" here: an empty
              // list and a failed load look identical to the clinician, and one
              // of them means the attached documents are still there.
              <div className='flex items-center gap-3'>
                <p className='text-sm text-destructive'>
                  Couldn&apos;t load documents.
                </p>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => void fetchDocuments()}
                >
                  Retry
                </Button>
              </div>
            ) : documents.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                No documents attached
              </p>
            ) : (
              <div className='divide-y rounded-md border'>
                {documents.map(doc => (
                  <div
                    key={doc.id}
                    className='group flex items-center gap-2 px-3 py-2'
                  >
                    <button
                      className='flex min-w-0 flex-1 items-center gap-2 text-left text-sm'
                      onClick={() => void openDocument(doc.id)}
                    >
                      <FileText className='h-4 w-4 shrink-0 text-blue-500' />
                      <span className='truncate'>{doc.file_name}</span>
                    </button>
                    {!isReadonly && (
                      <button
                        className='shrink-0 opacity-0 transition-opacity group-hover:opacity-100'
                        onClick={() => setPendingDeleteDocId(doc.id)}
                        aria-label={`Delete ${doc.file_name}`}
                      >
                        <Trash2 className='h-4 w-4 text-destructive' />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {!isReadonly && (
              <div>
                <input
                  ref={fileInputRef}
                  type='file'
                  className='hidden'
                  onChange={handleUpload}
                  accept='.pdf,.png,.jpg,.jpeg,.doc,.docx'
                />
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => fileInputRef.current?.click()}
                  className='flex items-center gap-1.5'
                >
                  <Upload className='h-3.5 w-3.5' />
                  Upload document
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4'>
            <EpisodeNotes
              episodeId={miscTask.episode_id}
              canAdd={!isDeleted}
              description='Shared by every task in this episode.'
            />
          </CardContent>
        </Card>
      </div>

      <AttestationDialog
        open={signAttestationOpen}
        requiresCredential={signRequiresESignature}
        attestation={miscAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={signingLoading}
        actionLabel={getSignButtonLabel(signTargetStatus)}
        // Clinician lane only -- see the prop's docs on AttestationDialog.
        showSignatureDate={signRequiresESignature}
        patientId={patientId}
        onConfirm={(credential, signatureDate) =>
          void doSignAndSubmit(credential, signatureDate)
        }
        onCancel={() => setSignAttestationOpen(false)}
      />

      <AttestationDialog
        open={approveAttestationOpen}
        requiresCredential={false}
        busyLabel='Approving…'
        attestation={miscAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={approving}
        actionLabel='Sign & Complete'
        onConfirm={credential => void doApprove(credential)}
        onCancel={() => setApproveAttestationOpen(false)}
      />

      {bypassDialog}

      <SignatureHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        taskId={miscTaskId}
        includeDeleted={isDeleted}
      />

      <WorkLogDialog
        open={workLogOpen}
        onOpenChange={setWorkLogOpen}
        taskId={miscTaskId}
        taskType='misc_task'
        includeDeleted={isDeleted}
      />

      <ConfirmationDialog
        open={pendingDeleteDocId !== null}
        title='Delete document'
        message='Are you sure you want to delete this document? This action cannot be undone.'
        confirmLabel='Delete'
        cancelLabel='Cancel'
        loading={deleting}
        onAccept={() => void handleDeleteDocument()}
        onCancel={() => setPendingDeleteDocId(null)}
      />
    </div>
  );
};
